import React, { useState } from "react";
import { Popover, Card } from "antd";
import { Link } from "react-router-dom";

// Utility function for className merging
const cn = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

type LinkPreviewProps = {
  children: React.ReactNode;
  url: string;
  className?: string;
  width?: number;
  height?: number;
} & (
  | { isStatic: true; imageSrc: string }
  | { isStatic?: false; imageSrc?: never }
);

export const LinkPreview = ({
  children,
  url,
  className,
  width = 200,
  height = 125,
  isStatic = false,
  imageSrc = "",
}: LinkPreviewProps) => {
  let src;
  if (!isStatic) {
    // Simple URL encoding for microlink.io API
    const params = new URLSearchParams({
      url,
      screenshot: "true",
      meta: "false",
      embed: "screenshot.url",
      colorScheme: "dark",
      "viewport.isMobile": "true",
      "viewport.deviceScaleFactor": "1",
      "viewport.width": (width * 3).toString(),
      "viewport.height": (height * 3).toString(),
    });
    src = `https://api.microlink.io/?${params.toString()}`;
  } else {
    src = imageSrc;
  }

  const [isMounted, setIsMounted] = useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const previewContent = (
    <Card
      style={{ width: width + 20, padding: 0 }}
      bodyStyle={{ padding: 4 }}
      cover={
        <img
          src={isStatic ? imageSrc : src}
          width={width}
          height={height}
          alt="preview image"
          style={{ borderRadius: "8px 8px 0 0" }}
          loading="eager"
        />
      }
    >
      <Link to={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    </Card>
  );

  return (
    <>
      {isMounted && (
        <div style={{ display: "none" }}>
          <img
            src={src}
            width={width}
            height={height}
            alt="hidden image"
            loading="eager"
          />
        </div>
      )}

      <Popover
        content={previewContent}
        trigger="hover"
        placement="top"
        mouseEnterDelay={0.1}
        mouseLeaveDelay={0.1}
      >
        <div
          className={cn(
            "text-green-600 cursor-pointer hover:text-green-800 transition-colors",
            className
          )}
        >
          {children}
        </div>
      </Popover>
    </>
  );
};
